/**
 * Persisted, versioned table-view state and the resolver's public model types.
 * The column settings meta lives on {@link ColumnType} in `../types`; this file
 * covers the user-state side of the contract.
 */

/**
 * Schema version of a persisted {@link TableViewState}. Bump when the shape
 * changes and add a matching step to the upgrader registry in `./upgrade`.
 * See `./table-view-state-versioning.md`.
 */
export const CURRENT_TABLE_VIEW_STATE_VERSION = 1 as const;

export interface TableViewStateV1 {
  v: 1;
  /**
   * Exhaustive membership set of visible leaf keys (settingsKey-aliased) — the
   * same semantics as the legacy `string[]`. Array order is meaningless;
   * ordering lives in `order`. `configurable: false` columns are forced visible
   * by the resolver regardless of this set.
   */
  visible: string[];
  /**
   * Leaf keys pinned. Entries for currently-hidden keys are kept — re-showing a
   * column restores its pin.
   */
  pinned?: string[];
  /**
   * Present only after the user actually reorders. Absent means the table
   * follows the live default (config) order, including future changes to it.
   */
  order?: {
    /** Group keys + standalone column keys. */
    topLevel?: string[];
    /** Per-group leaf order, only for groups the user has touched. */
    children?: Record<string, string[]>;
  };
  /**
   * Registered per-scope custom settings, owned by the app hook. The resolver
   * neither reads nor writes this; it is preserved untouched.
   */
  settings?: Record<string, unknown>;
}

/** Latest persisted shape. Widens to a union (V1 | V2 | …) as versions land. */
export type TableViewState = TableViewStateV1;

/**
 * Pinned-column render model. Only `'C'` (pin in place) is implemented in this
 * release; `'B1'`/`'B2'` (group split) are future strategy points over the same
 * persisted state — see the design doc §5. The strategy point is the internal
 * pinning transform, not a public parameter.
 */
export type TableRenderModel = 'C';

/** Panel item model produced by `deriveSettingsItems`. */
export interface SettingsItem {
  /** State-entry key: `settingsKey ?? key`. */
  key: string;
  /** `editTitle`, else a string `title`, else `key`. */
  label: string;
  visible: boolean;
  pinned: boolean;
  /** Effective (group-cascaded) `configurable !== false`; on a group item,
   *  true when any child can toggle. */
  canToggle: boolean;
  /** Effective (group-cascaded) `pinnable !== false`; on a group item, true
   *  when any child can pin. */
  canPin: boolean;
  /** `reorderable !== false`. */
  canReorder: boolean;
  /** Present for group columns; leaf items in config-adjacent order. */
  children?: SettingsItem[];
}
